import { useCallback, useEffect, useRef, useState } from 'react';
import { Text, UnstyledButton } from '@mantine/core';
import type { Story } from '@nelnela/contracts';
import { api } from '../../lib/api';
import { isPieceKey, pieceQueueLines, speakerOrder, voiceCount } from '../../lib/piece-player';
import {
  currentLine,
  currentUrl,
  ended,
  halt,
  initQueue,
  markReady,
  queueProgress,
  skipNext,
  skipPrev,
  start,
  toPrefetch,
  type QueueState,
} from '../../lib/tts-queue';
import { ttsCalmLine, ttsPlayer } from '../../lib/tts-player';

/**
 * Play a scripted piece aloud, sentence by sentence, each speaker in their own voice (M6b step 6).
 *
 * **This deliberately does not refactor `screens/Listen.tsx`,** which drives the same reducer over
 * a scene of a real transcript. Merging the two is the obvious move and the wrong one right now:
 * Listen shipped in M6a and has never been opened in a browser, and refactoring code nobody has
 * run — to save about sixty lines of glue — risks the app's other new audio screen for nothing.
 * The part where bugs actually live is `lib/tts-queue.ts`, and that IS shared, unchanged. Merge
 * the glue once Listen has been verified on production.
 */

export interface PiecePlayerProps {
  story: Pick<Story, 'id' | 'targetLang' | 'sentences' | 'voices' | 'kind'>;
  /** the sentence being spoken, or -1 — the reader draws it as the playing line (spec §4) */
  onSentence: (idx: number) => void;
  /** every clip that actually played, for the `replay` event the reader emits */
  onPlayed?: (sentenceIdx: number) => void;
}

export function PiecePlayer({ story, onSentence, onPlayed }: PiecePlayerProps) {
  const [queue, setQueue] = useState<QueueState>(() => initQueue(pieceQueueLines(story)));
  const [calm, setCalm] = useState<string | null>(null);

  // A different piece is a different queue. Keyed on the id rather than on `story`, whose identity
  // changes on every refetch — rebuilding mid-play would restart the piece from the top.
  const builtFor = useRef(story.id);
  useEffect(() => {
    if (builtFor.current === story.id) return;
    builtFor.current = story.id;
    setQueue(initQueue(pieceQueueLines(story)));
    setCalm(null);
  }, [story]);

  // ------------------------------------------------ synthesis
  const requested = useRef(new Set<string>());
  useEffect(() => {
    if (queue.status === 'halted' || queue.status === 'done') return;
    for (const line of toPrefetch(queue)) {
      if (requested.current.has(line.lineId)) continue;
      requested.current.add(line.lineId);
      void api
        .tts({
          targetLang: story.targetLang,
          text: line.text,
          ...(line.voice ? { voice: line.voice } : {}),
        })
        .then((audio) => setQueue((s) => markReady(s, line.lineId, audio.url)))
        .catch((err) => {
          // Every refusal this route gives — 403 unentitled, 429 the month's characters, 503 no
          // voice — is a fact about the voice and not about this line, so grinding through the
          // rest would produce sixteen identical failures. Halt and say it once; ▶ retries,
          // because the id comes back out of the pool.
          requested.current.delete(line.lineId);
          setCalm(ttsCalmLine(err));
          setQueue((s) => halt(s));
        });
    }
  }, [queue, story.targetLang]);

  // ------------------------------------------------ playing
  const startedKey = useRef<string | null>(null);
  useEffect(() => {
    if (queue.status !== 'playing') {
      if (queue.status !== 'idle') onSentence(-1);
      return;
    }
    const line = currentLine(queue);
    const url = currentUrl(queue);
    onSentence(line ? queue.idx : -1);
    if (!line || !url) return; // still synthesizing — the effect runs again on markReady
    if (startedKey.current === line.lineId) return;
    startedKey.current = line.lineId;
    const who = story.sentences[queue.idx]?.speaker;
    void ttsPlayer.play({
      url,
      title: line.text,
      subtitle: who ? `${who} · reading aloud` : 'reading aloud',
      key: line.lineId,
    });
    onPlayed?.(queue.idx);
  }, [queue, onSentence, onPlayed, story.sentences]);

  // The one edge that may advance. The key check is not defensive tidiness: `ttsPlayer` is a
  // single shared track, so a gloss sheet's "hear it" finishing would otherwise step the piece on.
  useEffect(
    () =>
      ttsPlayer.onEnded((finished) => {
        if (!isPieceKey(finished?.key)) return;
        setQueue((s) => ended(s));
      }),
    [],
  );

  // Leaving the reader stops the audio. A piece is not a podcast: it is the text in front of you.
  useEffect(() => () => ttsPlayer.stop(), []);

  const play = useCallback(() => {
    setCalm(null);
    setQueue((s) => start(s.status === 'done' ? initQueue(s.lines) : s));
  }, []);
  const pause = useCallback(() => {
    ttsPlayer.stop();
    setQueue((s) => halt(s));
  }, []);

  const playing = queue.status === 'playing';
  const speakers = speakerOrder(story);
  const voices = voiceCount(story);
  const progress = queueProgress(queue);

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        padding: '8px 14px',
        borderTop: '1px solid var(--nn-hairline)',
        background: 'var(--nn-panel)',
      }}
    >
      <UnstyledButton
        onClick={playing ? pause : play}
        aria-label={playing ? 'Pause' : 'Play the piece aloud'}
        style={{
          width: 38,
          height: 38,
          borderRadius: '50%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: 'var(--nn-accent)',
          color: '#fff',
          flexShrink: 0,
        }}
      >
        {playing ? <IconPause /> : <IconPlay />}
      </UnstyledButton>

      <div style={{ flex: 1, minWidth: 0 }}>
        <Text fz={12} c="dimmed" truncate>
          {calm ??
            (queue.status === 'done'
              ? 'Played to the end'
              : /* Only ever what is true of the audio: two speakers mapped to one voice is one
                   voice, and `voiceCount` counts the voices for that reason. */
                voices >= 2
                ? `${speakers.join(' · ')} — two voices`
                : story.kind === 'dialogue'
                  ? speakers.join(' · ')
                  : 'Read aloud')}
        </Text>
        <div
          aria-hidden
          style={{ height: 2, marginTop: 4, background: 'var(--nn-hairline)', borderRadius: 1 }}
        >
          <div
            style={{
              height: '100%',
              width: `${Math.round(progress * 100)}%`,
              background: 'var(--nn-accent)',
              borderRadius: 1,
              transition: 'width 160ms linear',
            }}
          />
        </div>
      </div>

      <UnstyledButton
        onClick={() => setQueue((s) => skipPrev(s))}
        aria-label="Previous sentence"
        style={STEP}
      >
        ⏮
      </UnstyledButton>
      <UnstyledButton
        onClick={() => setQueue((s) => skipNext(s))}
        aria-label="Next sentence"
        style={STEP}
      >
        ⏭
      </UnstyledButton>
    </div>
  );
}

const STEP: React.CSSProperties = {
  width: 34,
  height: 34,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  color: 'var(--nn-muted)',
  fontSize: 14,
  flexShrink: 0,
};

function IconPlay() {
  return (
    <svg width="15" height="15" viewBox="0 0 16 16" fill="currentColor" aria-hidden>
      <path d="M4 2.5v11l9-5.5-9-5.5z" />
    </svg>
  );
}
function IconPause() {
  return (
    <svg width="15" height="15" viewBox="0 0 16 16" fill="currentColor" aria-hidden>
      <path d="M4.5 2.5h2.5v11H4.5zM9 2.5h2.5v11H9z" />
    </svg>
  );
}
